"use client";

import { useState } from "react";
import { Checkbox, Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  PairedDataInput,
  usePairedData,
} from "@/components/tools/stats-descriptive/ui";
import { CurveResults } from "@/components/tools/stats-regression/curve-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type FitMethod,
  fitModels,
  MODEL_IDS,
  type ModelId,
} from "@/lib/tool-kit/stats-regression/curves";

/** English strings. A translated content file overrides these through `ui`. */
export const CURVE_FITTING_CALCULATOR_STRINGS = {
  entryLabel: "Data entry",
  entryLists: "Separate X and Y lists",
  entryLines: 'Pairs — one "x, y" per line',
  xLabel: "X values",
  yLabel: "Y values",
  xPlaceholder: "4, 7, 8, 9, 10, 11",
  yPlaceholder: "2, 4, 16, 10, 18, 17",
  pairsLabel: "Paired data — one x, y pair per line",
  pairsPlaceholder: "4, 2\n7, 4\n8, 16",
  valuesCount: "one: value|other: values",
  pairsCount: "one: pair read|other: pairs read",
  skippedCount:
    "one: , {count} line without two numbers skipped|other: , {count} lines without two numbers skipped",
  tryExample: "Try an example",
  exampleX:
    "4, 4, 7, 7, 8, 9, 10, 10, 10, 11, 11, 12, 12, 12, 12, 13, 13, 13, 13, 14, 14, 14, 14, 15, 15, 15, 16, 16, 17, 17, 17, 18, 18, 18, 18, 19, 19, 19, 20, 20, 20, 20, 20, 22, 23, 24, 24, 24, 24, 25",
  exampleY:
    "2, 10, 4, 22, 16, 10, 18, 26, 34, 17, 28, 14, 20, 24, 28, 26, 34, 34, 46, 26, 36, 60, 80, 20, 26, 54, 32, 40, 32, 40, 50, 42, 56, 76, 84, 36, 46, 68, 32, 48, 52, 56, 64, 66, 54, 70, 92, 93, 120, 85",
  examplePairs:
    "4, 2\n4, 10\n7, 4\n7, 22\n8, 16\n9, 10\n10, 18\n10, 26\n10, 34\n11, 17\n11, 28\n12, 14\n12, 20\n12, 24\n12, 28\n13, 26\n13, 34\n13, 34\n13, 46\n14, 26\n14, 36\n14, 60\n14, 80\n15, 20\n15, 26\n15, 54\n16, 32\n16, 40\n17, 32\n17, 40\n17, 50\n18, 42\n18, 56\n18, 76\n18, 84\n19, 36\n19, 46\n19, 68\n20, 32\n20, 48\n20, 52\n20, 56\n20, 64\n22, 66\n23, 54\n24, 70\n24, 92\n24, 93\n24, 120\n25, 85",
  clear: "Clear",
  mismatch:
    "The two lists differ in length (X: {x}, Y: {y}). Each x needs a matching y.",
  enterPrompt:
    "Enter at least four (x, y) pairs, with at least three different x values.",
  modelsLabel: "Models to fit",
  modelLinear: "Linear",
  modelPoly2: "Quadratic (degree 2)",
  modelPoly3: "Cubic (degree 3)",
  modelPoly4: "Polynomial, degree 4",
  modelPoly5: "Polynomial, degree 5",
  modelPoly6: "Polynomial, degree 6",
  modelExponential: "Exponential",
  modelLogarithmic: "Logarithmic",
  modelPower: "Power",
  methodLabel: "Exponential and power fits",
  methodOriginal: "Least squares on the original y (non-linear fit)",
  methodTransformed: "Straight line through ln y (like spreadsheet trendlines)",
  reasonNeedsPositiveX: "needs every x above 0",
  reasonNeedsPositiveY: "needs every y above 0",
  reasonNeedsMoreData: "needs more data points",
  reasonNeedsDistinctX: "needs more different x values",
  reasonFailed: "could not be fitted",
  notFittedTitle: "Not fitted",
  notFittedLine: "{model}: {reason}.",
  results: "Results",
  compareTitle: "Model comparison",
  colModel: "Model",
  colEquation: "Equation",
  colR2: "R²",
  colAdjR2: "Adjusted R²",
  colRmse: "RMSE",
  colAic: "AIC",
  colAicc: "AICc",
  colDelta: "Δ AICc",
  best: "{model} has the lowest AICc.",
  bestNote:
    "R² can only rise as you add polynomial terms, so it always favours the biggest model. AIC and AICc charge for each parameter; the lowest AICc is the best trade-off between fit and complexity, and differences below about 2 are not worth acting on. Judge by the plot as well.",
  eqPoly: "y = {terms}",
  eqExp: "y = {a} × e^({b} × x)",
  eqLog: "y = {a} + {b} × ln x",
  eqLogMinus: "y = {a} − {b} × ln x",
  eqPower: "y = {a} × x^({b})",
  termPlus: " + ",
  termMinus: " − ",
  detailTitle: "Details: {model}",
  chooseModel: "Show details for",
  equationLabel: "Fitted equation",
  paramsTitle: "Parameters",
  colTerm: "Term",
  colValue: "Estimate",
  colSe: "Standard error",
  colT: "t",
  colP: "p-value",
  rSquaredCard: "R² (original y)",
  rSquaredLogCard: "R² on the ln y scale",
  rmseCard: "Root mean squared error",
  sseCard: "Sum of squared errors",
  nCard: "Data points (n)",
  paramsCard: "Parameters estimated",
  transformedNote:
    "A transformed fit minimises squared errors in ln y, so it is judged on the log scale (the R² a spreadsheet trendline shows) and can fit y itself less well. The R² above is measured on the original y, which is the fair comparison with the other models.",
  illConditioned:
    "The expanded polynomial coefficients lose digits to cancellation for data like this (large x values far from zero). Predictions here use the centred form, y = Σ cⱼ((x − {centre}) ÷ {scale})ʲ, which is exact; rounding the equation above will not reproduce them.",
  notConverged:
    "The non-linear fit did not reach its tolerance; treat these estimates with caution.",
  plotTitle: "Data and fitted curves",
  plotLabel: "Scatter plot of {n} data points with the fitted curves",
  axisX: "x",
  axisY: "y",
  seriesData: "Data",
  residualPlotTitle: "Residuals of {model}",
  residualPlotLabel: "Residuals against x for {model}",
  axisResidual: "Residual (y − ŷ)",
  seriesZero: "Zero residual",
  residualNote:
    "Residuals should scatter evenly around zero. A run of positives then negatives means the curve's shape is wrong, whatever its R².",
  predictTitle: "Predict y for a new x",
  predictLabel: "New x value",
  predictedHeading: "Predicted ŷ by model",
  colPredicted: "Predicted ŷ",
  extrapolation:
    "{x} lies outside the observed x range ({min} to {max}). That is extrapolation: polynomial curves in particular can swing wildly beyond the data, and no model here knows what happens there.",
  residualsTitle: "Residuals",
  colIndex: "#",
  colX: "x",
  colY: "y",
  colFitted: "ŷ",
  colResidual: "Residual",
  residualsMore: "Showing the first {shown} of {total} points.",
  workingTitle: "How it was calculated",
  workPoly:
    "Polynomials are fitted by QR decomposition on powers of (x − x̄) ÷ half-range, so even a degree-6 fit to calendar years is accurate; the coefficients are then expanded to powers of x.",
  workLog: "The logarithmic model is a straight-line fit of y on ln x.",
  workExpPower:
    "The exponential and power models are non-linear. They are fitted by Levenberg–Marquardt least squares from the log-linear fit as the starting point, or, if you chose the transformed method, as a straight line through ln y (against x for exponential, against ln x for power).",
  workMetrics:
    "R² = 1 − SSE ÷ SST = 1 − {sse} ÷ {sst} = {r2}. AIC = −2 ln L + 2(k + 1), with ln L = −n ÷ 2 × (ln 2π + 1 − ln n + ln SSE) — the definition R uses; AICc = AIC + 2(k + 1)(k + 2) ÷ (n − k − 2).",
} as const;

export type CurveStrings = typeof CURVE_FITTING_CALCULATOR_STRINGS;

const DEFAULT_MODELS: ModelId[] = [
  "linear",
  "poly2",
  "poly3",
  "exponential",
  "logarithmic",
  "power",
];

export default function CurveFittingCalculatorWidget() {
  const strings = useToolUi(CURVE_FITTING_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const data = usePairedData();
  const [selected, setSelected] = useState<ModelId[]>(DEFAULT_MODELS);
  const [method, setMethod] = useState<FitMethod>("original");

  const modelName = (id: ModelId): string => {
    const names: Record<ModelId, string> = {
      linear: strings.modelLinear,
      poly2: strings.modelPoly2,
      poly3: strings.modelPoly3,
      poly4: strings.modelPoly4,
      poly5: strings.modelPoly5,
      poly6: strings.modelPoly6,
      exponential: strings.modelExponential,
      logarithmic: strings.modelLogarithmic,
      power: strings.modelPower,
    };
    return names[id];
  };
  const toggle = (id: ModelId) =>
    setSelected((list) =>
      list.includes(id) ? list.filter((m) => m !== id) : [...list, id]
    );

  const ready = !data.mismatched && data.xs.length >= 4;
  const ids = MODEL_IDS.filter((id) => selected.includes(id));
  const results = ready ? fitModels(ids, data.xs, data.ys, method) : [];
  const showMismatch =
    data.mismatched && data.listXCount > 0 && data.listYCount > 0;
  const count = (n: number) => (
    <>
      {fmt(n)}
      {tPlural(strings.valuesCount, locale, n)}
    </>
  );

  return (
    <>
      <section className="space-y-4">
        <PairedDataInput
          data={data}
          idPrefix="cf"
          labels={{
            mode: strings.entryLabel,
            lists: strings.entryLists,
            lines: strings.entryLines,
            x: strings.xLabel,
            y: strings.yLabel,
            xPlaceholder: strings.xPlaceholder,
            yPlaceholder: strings.yPlaceholder,
            pairs: strings.pairsLabel,
            pairsPlaceholder: strings.pairsPlaceholder,
          }}
          pairsHint={
            <>
              {fmt(data.pairCount)}
              {tPlural(strings.pairsCount, locale, data.pairCount)}
              {data.skippedLines > 0
                ? tPlural(strings.skippedCount, locale, data.skippedLines, {
                    count: fmt(data.skippedLines),
                  })
                : null}
            </>
          }
          xHint={count(data.listXCount)}
          yHint={count(data.listYCount)}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() =>
              data.fill({
                x: strings.exampleX,
                y: strings.exampleY,
                pairs: strings.examplePairs,
              })
            }
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={data.clear}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <fieldset className="space-y-2">
          <legend className={LABEL}>{strings.modelsLabel}</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {MODEL_IDS.map((id) => (
              <Checkbox
                checked={selected.includes(id)}
                key={id}
                label={modelName(id)}
                onChange={() => toggle(id)}
              />
            ))}
          </div>
        </fieldset>
        <PillGroup
          label={strings.methodLabel}
          name="cf-method"
          onChange={setMethod}
          options={[
            { value: "original", label: strings.methodOriginal },
            { value: "transformed", label: strings.methodTransformed },
          ]}
          value={method}
        />
      </section>

      {showMismatch ? (
        <Notice>
          {t(strings.mismatch, {
            x: fmt(data.listXCount),
            y: fmt(data.listYCount),
          })}
        </Notice>
      ) : null}
      {ready || showMismatch ? null : <Notice>{strings.enterPrompt}</Notice>}

      {ready && results.length > 0 ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <CurveResults
            fmt={fmt}
            modelName={modelName}
            results={results}
            strings={strings}
            xs={data.xs}
            ys={data.ys}
          />
        </section>
      ) : null}
    </>
  );
}
